import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Mail, CheckCircle, FileText, Receipt, Search, RefreshCw } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EmailSummaryCards = ({ emails }) => (
  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
    <div className="bg-white rounded-xl border p-4 flex items-center gap-3">
      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
        <Mail size={18} className="text-blue-600" />
      </div>
      <div>
        <p className="text-2xl font-bold text-gray-900">{emails.length}</p>
        <p className="text-xs text-gray-500">Total envoyés</p>
      </div>
    </div>
    <div className="bg-white rounded-xl border p-4 flex items-center gap-3">
      <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
        <FileText size={18} className="text-purple-600" />
      </div>
      <div>
        <p className="text-2xl font-bold text-gray-900">{emails.filter(e => e.doc_type === 'devis').length}</p>
        <p className="text-xs text-gray-500">Devis envoyés</p>
      </div>
    </div>
    <div className="bg-white rounded-xl border p-4 flex items-center gap-3">
      <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
        <Receipt size={18} className="text-green-600" />
      </div>
      <div>
        <p className="text-2xl font-bold text-gray-900">{emails.filter(e => e.doc_type === 'facture').length}</p>
        <p className="text-xs text-gray-500">Factures envoyées</p>
      </div>
    </div>
  </div>
);

const formatDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const EmailTable = ({ filtered }) => (
  <div className="bg-white rounded-xl border">
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-gray-50">
          <tr>
            <th className="text-left p-3">Document</th>
            <th className="text-left p-3">Destinataire</th>
            <th className="text-left p-3">Client</th>
            <th className="text-left p-3 hidden md:table-cell">Message</th>
            <th className="text-left p-3">Date</th>
            <th className="text-center p-3">Statut</th>
          </tr>
        </thead>
        <tbody>
          {filtered.map((email, i) => (
            <tr key={`email-${i}`} className="border-t hover:bg-gray-50" data-testid={`email-row-${i}`}>
              <td className="p-3">
                <div className="flex items-center gap-2">
                  <span className={`text-xs px-2 py-0.5 rounded font-medium ${email.doc_type === 'devis' ? 'bg-purple-100 text-purple-700' : 'bg-green-100 text-green-700'}`}>
                    {email.doc_type === 'devis' ? 'Devis' : 'Facture'}
                  </span>
                  <span className="font-mono font-medium text-gray-900">{email.doc_number}</span>
                </div>
              </td>
              <td className="p-3">
                <p className="text-gray-900">{email.recipient_email}</p>
                {email.recipient_name && <p className="text-xs text-gray-500">{email.recipient_name}</p>}
              </td>
              <td className="p-3 text-gray-600">{email.client_name}</td>
              <td className="p-3 hidden md:table-cell">
                <p className="text-gray-500 text-xs truncate max-w-[200px]">{email.message || '-'}</p>
              </td>
              <td className="p-3 text-gray-500 whitespace-nowrap">{formatDate(email.sent_at)}</td>
              <td className="p-3 text-center">
                <span className="inline-flex items-center gap-1 text-xs text-green-600 bg-green-50 px-2 py-0.5 rounded-full">
                  <CheckCircle size={10} /> Envoyé
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

export default function EmailHistoryModule({ authHeaders }) {
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/crm/email-history`, { headers: authHeaders });
      setEmails(res.data);
    } catch (err) {
      setEmails([]);
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  const filtered = emails.filter(e => {
    const matchType = !typeFilter || e.doc_type === typeFilter;
    const matchSearch = !search ||
      e.recipient_email?.toLowerCase().includes(search.toLowerCase()) ||
      e.client_name?.toLowerCase().includes(search.toLowerCase()) ||
      e.doc_number?.toLowerCase().includes(search.toLowerCase());
    return matchType && matchSearch;
  });

  return (
    <div data-testid="email-history-module">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-6">
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => setTypeFilter('')} className={`px-3 py-1.5 rounded-full text-xs font-medium ${!typeFilter ? 'bg-[#DC2626] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-testid="email-filter-all">Tous</button>
          <button onClick={() => setTypeFilter('devis')} className={`px-3 py-1.5 rounded-full text-xs font-medium ${typeFilter === 'devis' ? 'bg-[#DC2626] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-testid="email-filter-quotes">Devis</button>
          <button onClick={() => setTypeFilter('facture')} className={`px-3 py-1.5 rounded-full text-xs font-medium ${typeFilter === 'facture' ? 'bg-[#DC2626] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-testid="email-filter-invoices">Factures</button>
        </div>
        <div className="flex gap-2 items-center">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-8 pr-3 py-2 border rounded-lg text-sm w-60"
              data-testid="email-search"
            />
          </div>
          <button onClick={fetchHistory} className="p-2 hover:bg-gray-100 rounded-lg" title="Rafraîchir" data-testid="email-refresh">
            <RefreshCw size={16} className={loading ? 'animate-spin text-gray-400' : 'text-gray-600'} />
          </button>
        </div>
      </div>

      {loading && (
        <div className="text-center py-12 text-gray-400">Chargement...</div>
      )}
      {!loading && filtered.length === 0 && (
        <div className="bg-white rounded-xl border p-12 text-center">
          <Mail size={40} className="mx-auto text-gray-300 mb-3" />
          <p className="text-gray-400 text-sm">Aucun email envoyé</p>
        </div>
      )}
      {!loading && filtered.length > 0 && (
        <div className="space-y-3">
          <EmailSummaryCards emails={emails} />
          <EmailTable filtered={filtered} />
        </div>
      )}
    </div>
  );
}
